import React, { useState } from 'react';
import {
  Show,
  SimpleShowLayout,
  useRecordContext,
  useNotify,
  useRefresh,
  useRedirect,
  useTranslate
} from 'react-admin';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField as MuiTextField,
  Alert,
  Chip,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  IconButton,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Receipt,
  Person,
  Payment,
  LocalGasStation,
  Undo,
  ArrowBack,
  CheckCircle,
  Error as ErrorIcon,
  Schedule,
  Cancel,
  Fingerprint
} from '@mui/icons-material';
import { format } from 'date-fns';
import { API_CONFIG } from '../../../config/api';
import { tokenManager } from '../../../utils/tokenManager';

// Helper functions
const getStatusColor = (status: string) => {
  switch (status) {
    case 'completed': return 'success';
    case 'pending': return 'warning';
    case 'failed': return 'error';
    case 'cancelled': return 'default';
    case 'released': return 'info';
    default: return 'default';
  }
};

const getTypeColor = (type: string) => {
  switch (type) {
    case 'topup': return 'success';
    case 'deduction': return 'warning';
    case 'refund': return 'info';
    case 'pre_auth': return 'default';
    case 'reward': return 'secondary';
    default: return 'default';
  }
};

const detailsTableSx = {
  tableLayout: 'fixed',
  width: '100%',
  '& .MuiTableCell-root': {
    px: { xs: 1, sm: 2 },
    py: { xs: 1, sm: 1.25 },
    verticalAlign: 'top',
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
    fontSize: { xs: '0.75rem', sm: '0.875rem' },
  },
  '& code': {
    wordBreak: 'break-all',
    overflowWrap: 'anywhere',
    fontSize: { xs: '0.7rem', sm: '0.8rem' },
  },
};

const detailRowSx = {
  py: 1,
  px: 1,
  borderRadius: 1,
  border: '1px solid',
  borderColor: 'divider',
  backgroundColor: 'rgba(0, 0, 0, 0.01)',
};

const detailLabelSx = {
  fontSize: '0.72rem',
  color: 'text.secondary',
  fontWeight: 600,
  mb: 0.5,
  textTransform: 'uppercase',
  letterSpacing: '0.02em',
};

const detailValueSx = {
  fontSize: '0.875rem',
  wordBreak: 'break-word',
  overflowWrap: 'anywhere',
};

const wrappedChipSx = {
  maxWidth: '100%',
  height: 'auto',
  '& .MuiChip-label': {
    display: 'block',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
    lineHeight: 1.2,
    py: 0.5,
  },
};

// Page header component
const PageHeader = () => {
  const record = useRecordContext();
  const redirect = useRedirect();
  const translate = useTranslate();

  if (!record) return null;

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle />;
      case 'pending': return <Schedule />;
      case 'failed': return <ErrorIcon />;
      case 'cancelled': return <Cancel />;
      default: return <Receipt />;
    }
  };

  return (
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      mb: { xs: 2, sm: 4 },
      flexWrap: 'wrap',
      gap: 2,
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
        <IconButton
          onClick={() => redirect('/app-transactions')}
          size="large"
          sx={{
            flexShrink: 0,
            backgroundColor: '#FFFFFF',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            '&:hover': {
              backgroundColor: '#F5F5F5',
              boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
            },
          }}
        >
          <ArrowBack />
        </IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontWeight: 'bold',
              fontSize: { xs: '1.25rem', sm: '2.125rem' },
              wordBreak: 'break-word',
              overflowWrap: 'anywhere',
            }}
          >
            {translate('resources.app-transactions.show.title')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
            <Chip
              icon={getStatusIcon(record.status)}
              label={translate(`resources.app-transactions.statuses.${record.status}`, { _: record.status })}
              color={getStatusColor(record.status) as any}
              size="small"
              sx={wrappedChipSx}
            />
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{
                fontSize: { xs: '0.72rem', sm: '0.875rem' },
                wordBreak: 'break-word',
                overflowWrap: 'anywhere',
              }}
            >
              #{record.id}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// Transaction Info Card
const TransactionInfo = () => {
  const record = useRecordContext();
  const translate = useTranslate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  if (!record) return null;

  return (
    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box display="flex" alignItems="center" mb={3}>
          <Box sx={{
            width: { xs: 32, sm: 40 },
            height: { xs: 32, sm: 40 },
            borderRadius: 2,
            backgroundColor: 'primary.light',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: 2
          }}>
            <Receipt sx={{ color: 'primary.main' }} />
          </Box>
          <Typography variant="h6" fontWeight="600" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>{translate('resources.app-transactions.show.transactionInfo')}</Typography>
        </Box>
        {isMobile ? (
          <Box sx={{ display: 'grid', gap: 1 }}>
            <Box sx={detailRowSx}>
              <Typography sx={detailLabelSx}>{translate('resources.app-transactions.show.transactionId')}</Typography>
              <Typography sx={detailValueSx}>{record.id}</Typography>
            </Box>
            <Box sx={detailRowSx}>
              <Typography sx={detailLabelSx}>{translate('resources.app-transactions.show.type')}</Typography>
              <Chip
                label={translate(`resources.app-transactions.types.${record.type}`, { _: record.type })}
                color={getTypeColor(record.type) as any}
                size="small"
                sx={wrappedChipSx}
              />
            </Box>
            <Box sx={detailRowSx}>
              <Typography sx={detailLabelSx}>{translate('resources.app-transactions.show.status')}</Typography>
              <Chip
                label={translate(`resources.app-transactions.statuses.${record.status}`, { _: record.status })}
                color={getStatusColor(record.status) as any}
                size="small"
                sx={wrappedChipSx}
              />
            </Box>
            <Box sx={detailRowSx}>
              <Typography sx={detailLabelSx}>{translate('resources.app-transactions.show.amount')}</Typography>
              <Typography sx={{ ...detailValueSx, color: record.amount >= 0 ? 'success.main' : 'error.main', fontWeight: 600 }}>
                {record.amount >= 0 ? '+' : ''} HKD {Math.abs(record.amount).toFixed(2)}
              </Typography>
            </Box>
            <Box sx={detailRowSx}>
              <Typography sx={detailLabelSx}>{translate('resources.app-transactions.show.dateTime')}</Typography>
              <Typography sx={detailValueSx}>
                {record.timestamp ? format(new Date(record.timestamp), 'PPpp') : '-'}
              </Typography>
            </Box>
            <Box sx={detailRowSx}>
              <Typography sx={detailLabelSx}>{translate('resources.app-transactions.show.paymentMethod')}</Typography>
              <Typography sx={detailValueSx}>
                {record.payment_method ? translate(`resources.app-transactions.paymentMethods.${record.payment_method}`, { _: record.payment_method }) : '-'}
              </Typography>
            </Box>
            <Box sx={detailRowSx}>
              <Typography sx={detailLabelSx}>{translate('resources.app-transactions.show.referenceType')}</Typography>
              <Typography sx={detailValueSx}>{record.reference_type || '-'}</Typography>
            </Box>
            <Box sx={detailRowSx}>
              <Typography sx={detailLabelSx}>{translate('resources.app-transactions.show.referenceId')}</Typography>
              <Typography component="code" sx={detailValueSx}>{record.reference_id || '-'}</Typography>
            </Box>
          </Box>
        ) : (
          <TableContainer sx={{ width: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <Table size="small" sx={detailsTableSx}>
              <TableBody>
                <TableRow>
                  <TableCell sx={{ width: { xs: '35%', sm: '30%' } }}><strong>{translate('resources.app-transactions.show.transactionId')}</strong></TableCell>
                  <TableCell sx={{ wordBreak: 'break-word' }}>{record.id}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ width: { xs: '35%', sm: '30%' } }}><strong>{translate('resources.app-transactions.show.type')}</strong></TableCell>
                  <TableCell sx={{ wordBreak: 'break-word' }}>
                    <Chip
                      label={translate(`resources.app-transactions.types.${record.type}`, { _: record.type })}
                      color={getTypeColor(record.type) as any}
                      size="small"
                      sx={wrappedChipSx}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ width: { xs: '35%', sm: '30%' } }}><strong>{translate('resources.app-transactions.show.status')}</strong></TableCell>
                  <TableCell sx={{ wordBreak: 'break-word' }}>
                    <Chip
                      label={translate(`resources.app-transactions.statuses.${record.status}`, { _: record.status })}
                      color={getStatusColor(record.status) as any}
                      size="small"
                      sx={wrappedChipSx}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ width: { xs: '35%', sm: '30%' } }}><strong>{translate('resources.app-transactions.show.amount')}</strong></TableCell>
                  <TableCell sx={{ wordBreak: 'break-word' }}>
                    <Typography variant="h6" color={record.amount >= 0 ? 'success.main' : 'error.main'} fontWeight="600">
                      {record.amount >= 0 ? '+' : ''} HKD {Math.abs(record.amount).toFixed(2)}
                    </Typography>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ width: { xs: '35%', sm: '30%' } }}><strong>{translate('resources.app-transactions.show.dateTime')}</strong></TableCell>
                  <TableCell sx={{ wordBreak: 'break-word' }}>{record.timestamp ? format(new Date(record.timestamp), 'PPpp') : '-'}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ width: { xs: '35%', sm: '30%' } }}><strong>{translate('resources.app-transactions.show.paymentMethod')}</strong></TableCell>
                  <TableCell sx={{ wordBreak: 'break-word' }}>{record.payment_method ? translate(`resources.app-transactions.paymentMethods.${record.payment_method}`, { _: record.payment_method }) : '-'}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ width: { xs: '35%', sm: '30%' } }}><strong>{translate('resources.app-transactions.show.referenceType')}</strong></TableCell>
                  <TableCell sx={{ wordBreak: 'break-word' }}>{record.reference_type || '-'}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ width: { xs: '35%', sm: '30%' } }}><strong>{translate('resources.app-transactions.show.referenceId')}</strong></TableCell>
                  <TableCell sx={{ wordBreak: 'break-word' }}><code>{record.reference_id || '-'}</code></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
};

// User Info Card
const UserInfo = () => {
  const record = useRecordContext();
  const translate = useTranslate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  if (!record) return null;

  return (
    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box display="flex" alignItems="center" mb={3}>
          <Box sx={{
            width: { xs: 32, sm: 40 },
            height: { xs: 32, sm: 40 },
            borderRadius: 2,
            backgroundColor: 'info.light',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: 2
          }}>
            <Person sx={{ color: 'info.main' }} />
          </Box>
          <Typography variant="h6" fontWeight="600" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>{translate('resources.app-transactions.show.userInfo')}</Typography>
        </Box>
        {isMobile ? (
          <Box sx={{ display: 'grid', gap: 1 }}>
            <Box sx={detailRowSx}>
              <Typography sx={detailLabelSx}>{translate('resources.app-transactions.show.userId')}</Typography>
              <Typography sx={detailValueSx}>{record.user_id}</Typography>
            </Box>
            <Box sx={detailRowSx}>
              <Typography sx={detailLabelSx}>{translate('resources.app-transactions.show.name')}</Typography>
              <Typography sx={detailValueSx}>{record.user?.name || '-'}</Typography>
            </Box>
            <Box sx={detailRowSx}>
              <Typography sx={detailLabelSx}>{translate('resources.app-transactions.show.email')}</Typography>
              <Typography sx={detailValueSx}>{record.user?.email || '-'}</Typography>
            </Box>
          </Box>
        ) : (
          <TableContainer sx={{ width: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <Table size="small" sx={detailsTableSx}>
              <TableBody>
                <TableRow>
                  <TableCell sx={{ width: { xs: '35%', sm: '30%' } }}><strong>{translate('resources.app-transactions.show.userId')}</strong></TableCell>
                  <TableCell sx={{ wordBreak: 'break-word' }}>{record.user_id}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ width: { xs: '35%', sm: '30%' } }}><strong>{translate('resources.app-transactions.show.name')}</strong></TableCell>
                  <TableCell sx={{ wordBreak: 'break-word' }}>{record.user?.name || '-'}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ width: { xs: '35%', sm: '30%' } }}><strong>{translate('resources.app-transactions.show.email')}</strong></TableCell>
                  <TableCell sx={{ wordBreak: 'break-word' }}>{record.user?.email || '-'}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
};

// Payment Details Card
const PaymentDetails = () => {
  const record = useRecordContext();
  const translate = useTranslate();
  if (!record || !record.payment_details) return null;

  return (
    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box display="flex" alignItems="center" mb={3}>
          <Box sx={{
            width: { xs: 32, sm: 40 },
            height: { xs: 32, sm: 40 },
            borderRadius: 2,
            backgroundColor: 'warning.light',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: 2
          }}>
            <Payment sx={{ color: 'warning.main' }} />
          </Box>
          <Typography variant="h6" fontWeight="600" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>{translate('resources.app-transactions.show.paymentDetails')}</Typography>
        </Box>
        <Paper sx={{ bgcolor: 'grey.50', p: 2 }}>
          <pre style={{ margin: 0, fontSize: '0.75rem', maxWidth: '100%', wordBreak: 'break-all', overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }}>
            {JSON.stringify(record.payment_details, null, 2)}
          </pre>
        </Paper>
      </CardContent>
    </Card>
  );
};

// Charging Session Details Card
const ChargingSessionDetails = () => {
  const record = useRecordContext();
  const translate = useTranslate();
  if (!record || !record.session_details) return null;

  return (
    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box display="flex" alignItems="center" mb={3}>
          <Box sx={{
            width: { xs: 32, sm: 40 },
            height: { xs: 32, sm: 40 },
            borderRadius: 2,
            backgroundColor: 'success.light',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: 2
          }}>
            <LocalGasStation sx={{ color: 'success.main' }} />
          </Box>
          <Typography variant="h6" fontWeight="600" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>{translate('resources.app-transactions.show.chargingSessionDetails')}</Typography>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 1.5, sm: 2 } }}>
          <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 45%' }, minWidth: { xs: 0, sm: 200 } }}>
            <Typography color="textSecondary" gutterBottom>{translate('resources.app-transactions.show.sessionId')}</Typography>
            <Typography variant="body1" sx={detailValueSx}>{record.session_details.app_session_id}</Typography>
          </Box>
          <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 45%' }, minWidth: { xs: 0, sm: 200 } }}>
            <Typography color="textSecondary" gutterBottom>{translate('resources.app-transactions.show.charger')}</Typography>
            <Typography variant="body1" sx={detailValueSx}>{record.session_details.charger_name}</Typography>
          </Box>
          <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 45%' }, minWidth: { xs: 0, sm: 200 } }}>
            <Typography color="textSecondary" gutterBottom>{translate('resources.app-transactions.show.site')}</Typography>
            <Typography variant="body1" sx={detailValueSx}>{record.session_details.site_name}</Typography>
          </Box>
          <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 45%' }, minWidth: { xs: 0, sm: 200 } }}>
            <Typography color="textSecondary" gutterBottom>{translate('resources.app-transactions.show.energyDelivered')}</Typography>
            <Typography variant="body1" sx={detailValueSx}>{record.session_details.energy_delivered?.toFixed(3)} kWh</Typography>
          </Box>
          <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 45%' }, minWidth: { xs: 0, sm: 200 } }}>
            <Typography color="textSecondary" gutterBottom>{translate('resources.app-transactions.show.startTime')}</Typography>
            <Typography variant="body1" sx={detailValueSx}>
              {format(new Date(record.session_details.start_timestamp), 'PPp')}
            </Typography>
          </Box>
          <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 45%' }, minWidth: { xs: 0, sm: 200 } }}>
            <Typography color="textSecondary" gutterBottom>{translate('resources.app-transactions.show.endTime')}</Typography>
            <Typography variant="body1" sx={detailValueSx}>
              {record.session_details.charging_completed_at
                ? format(new Date(record.session_details.charging_completed_at), 'PPp')
                : translate('resources.app-transactions.show.inProgress')}
            </Typography>
          </Box>
          <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 45%' }, minWidth: { xs: 0, sm: 200 } }}>
            <Typography color="textSecondary" gutterBottom>{translate('resources.app-transactions.show.duration')}</Typography>
            <Typography variant="body1" sx={detailValueSx}>
              {record.session_details.duration_seconds
                ? `${Math.floor(record.session_details.duration_seconds / 60)} ${translate('resources.app-transactions.show.minutes')}`
                : '-'}
            </Typography>
          </Box>
          <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 45%' }, minWidth: { xs: 0, sm: 200 } }}>
            <Typography color="textSecondary" gutterBottom>{translate('resources.app-transactions.show.finalCost')}</Typography>
            <Typography variant="body1" sx={detailValueSx}>HKD {record.session_details.final_cost?.toFixed(2) || '0.00'}</Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

// Admin Notes Card
const AdminNotes = () => {
  const record = useRecordContext();
  const translate = useTranslate();
  if (!record || !record.admin_notes) return null;

  return (
    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box display="flex" alignItems="center" mb={2}>
          <Box sx={{
            width: { xs: 32, sm: 40 },
            height: { xs: 32, sm: 40 },
            borderRadius: 2,
            backgroundColor: 'grey.200',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: 2
          }}>
            <Fingerprint sx={{ color: 'grey.700' }} />
          </Box>
          <Typography variant="h6" fontWeight="600" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>{translate('resources.app-transactions.show.adminNotes')}</Typography>
        </Box>
        <Typography variant="body1" sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{record.admin_notes}</Typography>
      </CardContent>
    </Card>
  );
};

// Refund Actions Card
const RefundActions = () => {
  const record = useRecordContext();
  const translate = useTranslate();
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const notify = useNotify();
  const refresh = useRefresh();

  if (!record) return null;

  const canRefund = record.type === 'deduction' && record.status === 'completed';
  if (!canRefund) return null;

  const handleRefund = async () => {
    // Client-side validation — the backend enforces the same checks (positive
    // amount, non-empty reason, currency presence), but failing here gives the
    // operator a faster, clearer error than waiting for the round-trip.
    const parsedAmount = parseFloat(refundAmount);
    const trimmedReason = refundReason.trim();
    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
      notify(translate('resources.app-transactions.messages.refundAmountInvalid', { _: 'Refund amount must be a positive number' }), { type: 'error' });
      return;
    }
    if (!trimmedReason) {
      notify(translate('resources.app-transactions.messages.refundReasonRequired', { _: 'Refund reason is required' }), { type: 'error' });
      return;
    }

    try {
      const token = await tokenManager.getAccessToken();
      const response = await fetch(`${API_CONFIG.baseURL}/api/backend/app-transactions/refund`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify({
          transaction_id: record.id,
          amount: parsedAmount,
          reason: trimmedReason,
          notify_user: true
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || translate('resources.app-transactions.messages.refundFailed'));
      }

      notify(translate('resources.app-transactions.messages.refundSuccess'), { type: 'success' });
      setRefundDialogOpen(false);
      refresh();
    } catch (error: any) {
      notify(`${translate('resources.app-transactions.messages.refundFailed')}: ${error.message}`, { type: 'error' });
    }
  };

  return (
    <>
      <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Box display="flex" gap={2} sx={{ width: '100%' }}>
            <Button
              variant="contained"
              color="warning"
              startIcon={<Undo />}
              onClick={() => setRefundDialogOpen(true)}
              size="large"
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              {translate('resources.app-transactions.show.processRefund')}
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Dialog open={refundDialogOpen} onClose={() => setRefundDialogOpen(false)}>
        <DialogTitle>{translate('resources.app-transactions.show.processRefund')}</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {translate('resources.app-transactions.show.refundWarning')}
          </Alert>
          <MuiTextField
            fullWidth
            label={translate('resources.app-transactions.show.refundAmount')}
            type="number"
            value={refundAmount}
            onChange={(e) => setRefundAmount(e.target.value)}
            onWheel={(e) => (e.target as HTMLInputElement).blur()}
            InputProps={{
              inputProps: {
                max: Math.abs(record.amount),
                min: 0.01,
                step: 0.01
              }
            }}
            helperText={`${translate('resources.app-transactions.show.maximum')}: HKD ${Math.abs(record.amount)}`}
            sx={{ mb: 2 }}
          />
          <MuiTextField
            fullWidth
            label={translate('resources.app-transactions.show.refundReason')}
            multiline
            rows={3}
            value={refundReason}
            onChange={(e) => setRefundReason(e.target.value)}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRefundDialogOpen(false)}>{translate('resources.app-transactions.show.cancel')}</Button>
          <Button
            onClick={handleRefund}
            variant="contained"
            color="warning"
            disabled={
              !refundAmount ||
              !refundReason.trim() ||
              !Number.isFinite(parseFloat(refundAmount)) ||
              parseFloat(refundAmount) <= 0 ||
              parseFloat(refundAmount) > Math.abs(record.amount)
            }
          >
            {translate('resources.app-transactions.show.processRefund')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// Main Transaction Details Component
const TransactionDetails = () => {
  return (
    <Box sx={{ width: '100%', maxWidth: 800, margin: '0 auto', minWidth: 0 }}>
      <PageHeader />
      <TransactionInfo />
      <UserInfo />
      <PaymentDetails />
      <ChargingSessionDetails />
      <AdminNotes />
      <RefundActions />
    </Box>
  );
};

export const AppTransactionsShow = () => (
  <Show
    title={false}
    actions={false}
    component="div"
    sx={{
      backgroundColor: 'transparent',
      maxWidth: '100%',
      '& .RaShow-main': {
        backgroundColor: 'transparent',
        boxShadow: 'none',
        maxWidth: '100%',
      },
    }}
  >
    <SimpleShowLayout
      sx={{
        maxWidth: '100%',
        '& .MuiPaper-root': {
          backgroundColor: 'transparent',
          boxShadow: 'none',
        },
        '& > .MuiCardContent-root, & > div > .MuiCardContent-root': {
          px: { xs: 0, sm: 2 },
          py: { xs: 1, sm: 2 },
        },
        padding: { xs: 0, sm: 'inherit' },
      }}
    >
      <TransactionDetails />
    </SimpleShowLayout>
  </Show>
);

export default AppTransactionsShow;
